import { useEffect, useMemo, useState } from 'react';
import { useVisibleConversationRead } from '../hooks/useVisibleConversationRead';
import { NavLink, useParams } from 'react-router-dom';
import {
  useAuth,
  useMarkSquadRead,
  useSendSquadMessage,
  useSquadDetail,
  useSquadMembers,
  useSquadMessages,
  useSquadMemberPilotRating,
  useTenantBootstrap,
} from '@tark/shared/hooks';
import { ratingPilotEvidenceLabel, squadMemberPilotRatingHeadline } from '@tark/shared/lib';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Textarea } from '@tark/shared/components';
import type { Squad, SquadAddMemberPayload, SquadChatMessage, SquadMemberSummary, SquadRole } from '@tark/shared/types';
import {
  ArrowLeft,
  Building2,
  Calendar,
  FileSpreadsheet,
  Loader2,
  MailPlus,
  MapPin,
  MessageCircle,
  Reply,
  Send,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';

type ManageableSquadRole = Exclude<SquadRole, 'OWNER'>;

const MANAGEABLE_ROLES: ManageableSquadRole[] = ['MEMBER', 'MODERATOR', 'ADMIN'];
const SQUAD_ADMIN_SUCCESSION_RULE =
  'If an admin account is deleted, TARK continues with another active admin, then the longest-standing moderator, then the longest-standing active member. If no active member remains, the squad is archived.';
const PILOT_UI_ENABLED = import.meta.env.VITE_RATING_PILOT_UI_ENABLED === 'true';

function buildAdminPromotionConfirmation(displayName: string): string {
  return `Promote ${displayName} to Admin?\n\nThey can manage members, settings, sessions, and moderation. This adds another admin; it does not remove or replace you.\n\n${SQUAD_ADMIN_SUCCESSION_RULE}`;
}

function toManageableRole(role?: SquadRole | null): ManageableSquadRole {
  if (role === 'ADMIN' || role === 'MODERATOR' || role === 'MEMBER') {
    return role;
  }
  return 'MEMBER';
}

function formatCreatedAt(value?: string | null): string | null {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatJoinedAt(value?: string | null): string {
  if (!value) {
    return 'Joined recently';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return `Joined ${parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

function formatRoleLabel(role?: string | null): string {
  if (!role) {
    return 'Member';
  }

  return role
    .toLowerCase()
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function getRoleChipClasses(role?: string | null): string {
  if (role === 'ADMIN') {
    return 'border-red-500/30 bg-red-500/10 text-red-200';
  }

  if (role === 'MODERATOR') {
    return 'border-amber-500/30 bg-amber-500/10 text-amber-200';
  }

  return 'border-border bg-surface-elevated/50 text-text-muted';
}

function buildMemberMeta(member: SquadMemberSummary): string | null {
  const parts = [member.sports, member.tennisLevel, member.pickleballLevel].filter(Boolean);
  return parts.length ? parts.join(' • ') : null;
}

function getMemberDisplayName(member: SquadMemberSummary): string {
  if (member.fullProfileVisible === true && member.fullName?.trim()) {
    return member.fullName.trim();
  }
  return member.profileName?.trim() || `Member ${member.userId}`;
}

function getMemberInitials(member: SquadMemberSummary): string {
  const parts = getMemberDisplayName(member).split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts.length === 1
    ? parts[0].slice(0, 2)
    : `${parts[0][0]}${parts[parts.length - 1][0]}`
  ).toUpperCase();
}

type TenantConversationKind = 'coaching' | 'stringer' | 'seller' | 'participant';

function resolveTenantConversationKind(tenantArchetype?: string | null, directoryLabel?: string | null): TenantConversationKind {
  switch ((tenantArchetype || '').toUpperCase()) {
    case 'SERVICE_PROVIDER':
      return 'stringer';
    case 'COMMERCE':
      return 'seller';
    case 'EVENT_OPERATOR':
      return 'participant';
    default:
      if ((directoryLabel || '').toLowerCase() === 'clients') {
        return 'stringer';
      }
      if ((directoryLabel || '').toLowerCase() === 'customers') {
        return 'seller';
      }
      return 'coaching';
  }
}

function getTenantConversationCopy({
  kind,
  tenantName,
  workTabLabel,
  directoryLabel,
}: {
  kind: TenantConversationKind;
  tenantName?: string | null;
  workTabLabel: string;
  directoryLabel: string;
}) {
  switch (kind) {
    case 'stringer':
      return {
        entityLabel: 'conversation',
        groupContextLabel: 'Client Thread',
        memberSectionTitle: 'Conversation Participants',
        manageTitle: 'Add a teammate to this conversation',
        manageDescription: `Workspace conversations only accept users who already belong to ${tenantName || 'this service workspace'}.`,
        notesTitle: 'Stringer workspace notes',
        notes: [
          'Use this page to keep teammate access and private client follow-up organized around the service queue.',
          `${workTabLabel} stays official for turnaround and status, while chats keep pickup timing and clarifications human.`,
        ],
        linkedPrimaryDescription: `${workTabLabel} and ${directoryLabel} stay private to this workspace, even when public discovery starts in PROs.`,
      };
    case 'seller':
      return {
        entityLabel: 'conversation',
        groupContextLabel: 'Customer Thread',
        memberSectionTitle: 'Conversation Participants',
        manageTitle: 'Add a teammate to this conversation',
        manageDescription: `Workspace conversations only accept users who already belong to ${tenantName || 'this seller workspace'}.`,
        notesTitle: 'Seller workspace notes',
        notes: [
          'Use this page to keep teammate access and private buyer follow-up organized around the order/request flow.',
          `${workTabLabel} stays official for the queue, while chats keep buyer updates and internal coordination moving.`,
        ],
        linkedPrimaryDescription: `${workTabLabel} and ${directoryLabel} stay private to this workspace, even when public discovery starts in PROs.`,
      };
    case 'participant':
      return {
        entityLabel: 'conversation',
        groupContextLabel: 'Participant Thread',
        memberSectionTitle: 'Conversation Participants',
        manageTitle: 'Add a teammate to this conversation',
        manageDescription: `Workspace conversations only accept users who already belong to ${tenantName || 'this participant workspace'}.`,
        notesTitle: 'Participant workspace notes',
        notes: [
          'Use this page to keep participant and staff follow-up connected to the same event workspace.',
          `${workTabLabel} stays official for the event board, while chats handle human follow-up around it.`,
        ],
        linkedPrimaryDescription: `${workTabLabel} and ${directoryLabel} stay private to this workspace even when public visibility exists elsewhere.`,
      };
    default:
      return {
        entityLabel: 'group',
        groupContextLabel: 'Organization Group',
        memberSectionTitle: 'Group Members',
        manageTitle: 'Add an organization member to this group',
        manageDescription: `Tenant groups only accept users who already belong to ${tenantName || 'this organization'}.`,
        notesTitle: 'Coaching workspace notes',
        notes: [
          'This page works as the coach-side command surface for membership, linked context, and read-only chat activity.',
          `${workTabLabel} remains the source of truth for timing, while this group keeps coaches and guardians aligned before and after each session.`,
        ],
        linkedPrimaryDescription: `Schedule opens this group when a session occurrence is linked to the same tenant program, while ${directoryLabel} keeps roster relationships nearby.`,
      };
  }
}

export default function SquadDetailPage() {
  const { squadId } = useParams();
  const { user } = useAuth();
  const { activeTenant } = useTenantBootstrap(Boolean(user));
  const { data: squad, isLoading, isError, refetch } = useSquadDetail(squadId, Boolean(user && squadId));
  const {
    members,
    isLoading: membersLoading,
    addMember,
    updateRole,
    removeMember,
    isAddingMember,
    isUpdatingRole,
    isRemovingMember,
  } = useSquadMembers(squadId, Boolean(user && squadId));

  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [memberRole, setMemberRole] = useState<'MEMBER' | 'MODERATOR'>('MEMBER');
  const [inviteNote, setInviteNote] = useState('');
  const [memberNotice, setMemberNotice] = useState<string | null>(null);
  const [memberError, setMemberError] = useState<string | null>(null);
  const [roleDrafts, setRoleDrafts] = useState<Record<number, ManageableSquadRole>>({});

  const isTenantContext = Boolean(activeTenant?.tenant?.id);
  const workTabLabel = activeTenant?.tenant?.workTabLabel || 'Schedule';
  const directoryLabel = activeTenant?.tenant?.directoryTabLabel || 'Members';
  const tenantConversationKind = resolveTenantConversationKind(activeTenant?.tenant?.tenantArchetype, directoryLabel);
  const tenantConversationCopy = getTenantConversationCopy({
    kind: tenantConversationKind,
    tenantName: activeTenant?.tenant?.displayName,
    workTabLabel,
    directoryLabel,
  });
  const pageTitle = isTenantContext ? 'Chats' : 'Squads';
  const entityLabel = isTenantContext ? tenantConversationCopy.entityLabel : 'squad';
  const groupContextLabel = isTenantContext ? tenantConversationCopy.groupContextLabel : 'Squad';
  const canManageMembers = Boolean(squad?.userRole === 'ADMIN' || squad?.userRole === 'MODERATOR');
  const numericSquadId = Number(squadId);
  const canReplyFromWeb = Boolean(
    isTenantContext &&
      activeTenant?.roleCode &&
      ['OWNER', 'ADMIN', 'COACH', 'STAFF'].includes(activeTenant.roleCode.toUpperCase())
  );
  const createdAt = formatCreatedAt(squad?.createdAt);

  useEffect(() => {
    if (!members.length) {
      return;
    }

    setRoleDrafts((current) => {
      const next = { ...current };
      for (const member of members) {
        if (!next[member.userId]) {
          next[member.userId] = toManageableRole(member.role);
        }
      }
      return next;
    });
  }, [members]);

  const sortedMembers = useMemo(
    () =>
      [...members].sort((left, right) => {
        if (left.role === 'ADMIN' && right.role !== 'ADMIN') {
          return -1;
        }
        if (right.role === 'ADMIN' && left.role !== 'ADMIN') {
          return 1;
        }
        if (left.role === 'MODERATOR' && right.role === 'MEMBER') {
          return -1;
        }
        if (right.role === 'MODERATOR' && left.role === 'MEMBER') {
          return 1;
        }
        return getMemberDisplayName(left).localeCompare(getMemberDisplayName(right));
      }),
    [members]
  );

  const memberStats = useMemo(
    () => ({
      admins: members.filter((member) => member.role === 'ADMIN').length,
      moderators: members.filter((member) => member.role === 'MODERATOR').length,
      members: members.filter((member) => member.role === 'MEMBER').length,
    }),
    [members]
  );

  const quickStats = useMemo(
    () => [
      {
        label: isTenantContext ? tenantConversationCopy.memberSectionTitle : 'Squad Members',
        value: squad?.memberCount ?? 0,
        description: `Visible participants in this ${entityLabel}.`,
      },
      {
        label: isTenantContext ? (tenantConversationKind === 'coaching' ? 'Program Link' : 'Work Link') : 'Privacy',
        value: isTenantContext ? (tenantConversationKind === 'coaching' ? (squad?.linkedProgramId ? 'Linked' : 'None') : workTabLabel) : (squad?.privacyType || squad?.privacy || 'Private'),
        description: isTenantContext
          ? tenantConversationKind === 'coaching'
            ? 'Schedule can open this chat automatically when a program is linked.'
            : `${workTabLabel} and this conversation stay inside the same private workspace.`
          : 'Community squads stay independent of tenant scheduling.',
      },
      {
        label: 'Your Role',
        value: formatRoleLabel(squad?.userRole),
        description: squad?.isMember
          ? `You currently belong to this ${entityLabel}.`
          : `You are viewing this ${entityLabel} shell without an active membership.`,
      },
    ],
    [entityLabel, isTenantContext, squad, tenantConversationCopy.memberSectionTitle, tenantConversationKind, workTabLabel]
  );

  const handleAddMember = async () => {
    const payload: SquadAddMemberPayload = {
      emailOrPhone: emailOrPhone.trim(),
      role: memberRole,
      message: inviteNote.trim() || undefined,
    };

    if (!payload.emailOrPhone) {
      setMemberError(`Enter the email or phone for the ${entityLabel} member.`);
      return;
    }

    setMemberError(null);
    setMemberNotice(null);

    try {
      const created = await addMember(payload);
      setEmailOrPhone('');
      setInviteNote('');
      setMemberRole('MEMBER');
      setRoleDrafts((current) => ({
        ...current,
        [created.userId]: toManageableRole(created.role),
      }));
      setMemberNotice(`${getMemberDisplayName(created)} is now in this ${entityLabel}.`);
    } catch (error: any) {
      setMemberError(error?.response?.data?.message || error?.message || `Could not add this ${entityLabel} member.`);
    }
  };

  const handleSaveRole = async (member: SquadMemberSummary) => {
    const nextRole = roleDrafts[member.userId];
    if (!nextRole || nextRole === member.role) {
      return;
    }

    if (
      !isTenantContext &&
      nextRole === 'ADMIN' &&
      !window.confirm(buildAdminPromotionConfirmation(getMemberDisplayName(member)))
    ) {
      return;
    }

    setMemberError(null);
    setMemberNotice(null);

    try {
      const updated = await updateRole({
        userId: member.userId,
        newRole: nextRole,
      });
      setRoleDrafts((current) => ({
        ...current,
        [member.userId]: toManageableRole(updated.role),
      }));
      setMemberNotice(`Updated ${getMemberDisplayName(updated)} to ${formatRoleLabel(updated.role)}.`);
    } catch (error: any) {
      setRoleDrafts((current) => ({
        ...current,
        [member.userId]: toManageableRole(member.role),
      }));
      setMemberError(error?.response?.data?.message || error?.message || 'Could not update that role.');
    }
  };

  const handleRemoveMember = async (member: SquadMemberSummary) => {
    if (!window.confirm(`Remove ${getMemberDisplayName(member)} from the ${entityLabel}?`)) {
      return;
    }

    setMemberError(null);
    setMemberNotice(null);

    try {
      await removeMember(member.userId);
      setMemberNotice(`${getMemberDisplayName(member)} was removed from the ${entityLabel}.`);
    } catch (error: any) {
      setMemberError(error?.response?.data?.message || error?.message || 'Could not remove that member.');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !squad) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center p-8">
        <Card variant="default" className="max-w-lg w-full">
          <CardHeader>
            <CardTitle>{isTenantContext ? 'Conversation unavailable' : 'Squad unavailable'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-text-muted">
              We could not load this {entityLabel} in the current context. Try reloading or go back to the list.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => refetch()}>Retry</Button>
              <NavLink
                to="/squads"
                className="inline-flex h-9 items-center justify-center rounded-lg border border-border px-4 text-sm font-medium text-text transition-all duration-200 hover:border-primary/40 hover:text-primary"
              >
                Back to {pageTitle}
              </NavLink>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-6 md:px-8 md:py-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="md:hidden">
          <NavLink to="/squads" className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-text">
            <ArrowLeft className="h-4 w-4" />
            <span>{pageTitle}</span>
          </NavLink>
        </div>

        <Card variant="glass" className="overflow-hidden">
          <CardContent className="space-y-5">
            <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-3xl bg-primary/10 text-2xl font-semibold text-primary">
                  {(squad.name || 'G').charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-sm uppercase tracking-[0.18em] text-text-muted">{groupContextLabel}</p>
                  <h1 className="mt-2 text-3xl font-bold tracking-tight text-text">{squad.name}</h1>
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-text-muted">
                    <span>{squad.memberCount} members</span>
                    <span>•</span>
                    <span>{squad.sport || squad.sportType || 'Sport'}</span>
                    <span>•</span>
                    <span>{squad.privacyType || squad.privacy || 'PRIVATE'}</span>
                    {createdAt ? (
                      <>
                        <span>•</span>
                        <span>Created {createdAt}</span>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                {!isTenantContext && squad.userRole === 'ADMIN' ? (
                  <NavLink to={`/squads/${squad.id}/import-history`}>
                    <Button size="sm" variant="outline" leftIcon={<FileSpreadsheet className="h-4 w-4" />}>
                      Import history
                    </Button>
                  </NavLink>
                ) : null}
                {isTenantContext ? (
                  <>
                    <NavLink
                      to="/schedule"
                      className="inline-flex h-9 items-center justify-center rounded-lg border border-border px-4 text-sm font-medium text-text transition-all duration-200 hover:border-primary/40 hover:text-primary"
                    >
                      Open {workTabLabel}
                    </NavLink>
                    <NavLink
                      to="/directory"
                      className="inline-flex h-9 items-center justify-center rounded-lg border border-border px-4 text-sm font-medium text-text transition-all duration-200 hover:border-primary/40 hover:text-primary"
                    >
                      Open {directoryLabel}
                    </NavLink>
                  </>
                ) : null}
                <NavLink
                  to="/squads"
                  className="inline-flex h-9 items-center justify-center rounded-lg border border-border px-4 text-sm font-medium text-text transition-all duration-200 hover:border-primary/40 hover:text-primary"
                >
                  Back to {pageTitle}
                </NavLink>
              </div>
            </div>

            {squad.description ? (
              <p className="max-w-3xl text-sm leading-6 text-text-muted">{squad.description}</p>
            ) : null}

            <div className="grid gap-3 md:grid-cols-3">
              {quickStats.map((stat) => (
                <div key={stat.label} className="rounded-2xl border border-border bg-surface-elevated/40 p-4">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-text-subtle">{stat.label}</p>
                  <p className="mt-2 text-2xl font-semibold tracking-tight text-text">{stat.value}</p>
                  <p className="mt-2 text-sm text-text-muted">{stat.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
          <div className="space-y-6">
            <ReadOnlyConversationCard
              squad={squad}
              entityLabel={entityLabel}
              canReplyFromWeb={canReplyFromWeb}
            />

            <Card variant="default">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  {isTenantContext ? tenantConversationCopy.memberSectionTitle : 'Squad Members'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!isTenantContext && canManageMembers ? (
                  <p className="text-sm leading-6 text-text-muted">
                    Promoting someone adds another admin; it does not remove or replace you. {SQUAD_ADMIN_SUCCESSION_RULE}
                  </p>
                ) : null}
                {memberNotice ? (
                  <div className="rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-200">
                    {memberNotice}
                  </div>
                ) : null}
                {memberError ? (
                  <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                    {memberError}
                  </div>
                ) : null}

                {isTenantContext && canManageMembers ? (
                  <div className="rounded-2xl border border-border bg-surface-elevated/40 p-4">
                    <div className="flex items-center gap-2">
                      <MailPlus className="h-4 w-4 text-primary" />
                      <p className="text-sm font-semibold text-text">{tenantConversationCopy.manageTitle}</p>
                    </div>
                    <p className="mt-2 text-sm leading-6 text-text-muted">
                      {tenantConversationCopy.manageDescription}
                    </p>
                    <div className="mt-4 grid gap-4">
                      <Input
                        label="Email or phone"
                        placeholder="parent@example.com or +1 555 555 5555"
                        value={emailOrPhone}
                        onChange={(event) => setEmailOrPhone(event.target.value)}
                      />
                      <div className="grid gap-4 md:grid-cols-[0.35fr_0.65fr]">
                        <div className="space-y-1.5">
                          <label className="block text-sm font-medium text-text">Role</label>
                          <select
                            value={memberRole}
                            onChange={(event) => setMemberRole(event.target.value as 'MEMBER' | 'MODERATOR')}
                            className="h-10 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                          >
                            <option value="MEMBER">Member</option>
                            <option value="MODERATOR">Moderator</option>
                          </select>
                        </div>
                        <Textarea
                          label="Welcome note"
                          placeholder={`Optional note for the ${entityLabel}`}
                          value={inviteNote}
                          onChange={(event) => setInviteNote(event.target.value)}
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button onClick={() => void handleAddMember()} isLoading={isAddingMember}>
                          Add to {isTenantContext ? 'Group' : 'Squad'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : null}

                {membersLoading ? (
                  <div className="flex min-h-[180px] items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : sortedMembers.length ? (
                  <div className="space-y-3">
                    {sortedMembers.map((member) => {
                      const memberMeta = buildMemberMeta(member);
                      const memberDisplayName = getMemberDisplayName(member);
                      const draftRole = roleDrafts[member.userId] || member.role;
                      const canEditRole = Boolean(member.canPromote);
                      const canRemoveMember = Boolean(member.canRemove);

                      return (
                        <div key={member.userId} className="rounded-2xl border border-border bg-surface-elevated/40 p-4">
                          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                            <div className="flex min-w-0 gap-3">
                              {member.fullProfileVisible === true && member.profilePhoto ? (
                                <img
                                  src={member.profilePhoto}
                                  alt=""
                                  className="h-11 w-11 shrink-0 rounded-full object-cover"
                                />
                              ) : (
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">
                                  {getMemberInitials(member)}
                                </div>
                              )}
                              <div className="min-w-0 space-y-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="truncate text-sm font-semibold text-white">
                                    {memberDisplayName}
                                  </p>
                                <span
                                  className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${getRoleChipClasses(member.role)}`}
                                >
                                  {formatRoleLabel(member.role)}
                                </span>
                              </div>
                                {member.fullProfileVisible === true && member.email ? <p className="text-sm text-text-muted">{member.email}</p> : null}
                                <p className="text-xs text-text-subtle">{formatJoinedAt(member.joinedAt)}</p>
                                {memberMeta ? <p className="text-xs text-text-subtle">{memberMeta}</p> : null}
                                {!isTenantContext && String(member.userId) !== String(user?.id) ? (
                                  <SquadMemberPilotRatingDisclosure
                                    squadId={numericSquadId}
                                    targetUserId={member.userId}
                                  />
                                ) : null}
                              </div>
                            </div>

                            {(canEditRole || canRemoveMember) ? (
                              <div className="flex flex-col gap-3 sm:min-w-[240px]">
                                {canEditRole ? (
                                  <div className="flex items-center gap-2">
                                    <select
                                      value={draftRole}
                                      onChange={(event) =>
                                        setRoleDrafts((current) => ({
                                          ...current,
                                          [member.userId]: event.target.value as ManageableSquadRole,
                                        }))
                                      }
                                      className="h-9 flex-1 rounded-lg border border-border bg-surface px-3 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                    >
                                      {MANAGEABLE_ROLES.map((roleCode) => (
                                        <option key={roleCode} value={roleCode}>
                                          {formatRoleLabel(roleCode)}
                                        </option>
                                      ))}
                                    </select>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled={draftRole === member.role}
                                      isLoading={isUpdatingRole}
                                      onClick={() => void handleSaveRole(member)}
                                    >
                                      Save
                                    </Button>
                                  </div>
                                ) : null}

                                {canRemoveMember ? (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="justify-center text-red-200 hover:text-red-100 hover:bg-red-500/10"
                                    isLoading={isRemovingMember}
                                    onClick={() => void handleRemoveMember(member)}
                                  >
                                    Remove Member
                                  </Button>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-text-muted">No members are active in this {entityLabel} yet.</p>
                )}
              </CardContent>
            </Card>

            <Card variant="default">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-primary" />
                  {isTenantContext ? tenantConversationCopy.notesTitle : 'Squad Overview'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-text-muted">
                {isTenantContext ? (
                  <>
                    {tenantConversationCopy.notes.map((note) => (
                      <p key={note}>{note}</p>
                    ))}
                    <div className="flex flex-wrap gap-3 pt-2">
                      <NavLink to="/schedule">
                        <Button size="sm" variant="outline">Review {workTabLabel}</Button>
                      </NavLink>
                      <NavLink to="/directory">
                        <Button size="sm" variant="outline">Review {directoryLabel}</Button>
                      </NavLink>
                    </div>
                  </>
                ) : (
                  <>
                    <p>Community squads continue to power casual player groups, session coordination, and chat.</p>
                    <p>Use the mobile app for the richer chat experience; this web page is the current shell for reviewing context and membership.</p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card variant="default">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  Linked Context
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-text-muted">
                {isTenantContext ? (
                  <>
                    <div className="flex items-start gap-3">
                      <Building2 className="mt-0.5 h-4 w-4 text-primary" />
                      <div>
                        <p className="font-medium text-text">{activeTenant?.tenant?.displayName || 'Organization'}</p>
                        <p>Active tenant context determines which groups appear here and who can be added to them.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Calendar className="mt-0.5 h-4 w-4 text-primary" />
                      <div>
                        <p className="font-medium text-text">
                          {tenantConversationKind === 'coaching'
                            ? squad.linkedProgramId
                              ? `Program #${squad.linkedProgramId}`
                              : 'No linked program'
                            : workTabLabel}
                        </p>
                        <p>{tenantConversationCopy.linkedPrimaryDescription}</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <p>Community squads stay outside tenant mode and remain available whenever you switch back to Community context.</p>
                )}

                {squad.location ? (
                  <div className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-4 w-4 text-primary" />
                    <div>
                      <p className="font-medium text-text">Location</p>
                      <p>{squad.location}</p>
                    </div>
                  </div>
                ) : null}

                {squad.aiChatGuardEnabled != null ? (
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="mt-0.5 h-4 w-4 text-primary" />
                    <div>
                      <p className="font-medium text-text">Chat Guard</p>
                      <p>{squad.aiChatGuardEnabled ? 'Enabled for this group' : 'Disabled for this group'}</p>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <Card variant="default">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Membership Snapshot
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4">
                <div className="rounded-2xl border border-border bg-surface-elevated/40 p-4">
                  <p className="text-xs uppercase tracking-wide text-text-muted">Member Count</p>
                  <p className="mt-2 text-2xl font-bold text-text">{squad.memberCount}</p>
                  <p className="mt-2 text-sm text-text-muted">Current participants with access to this {entityLabel}.</p>
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="rounded-2xl border border-border bg-surface-elevated/40 p-4">
                    <p className="text-xs uppercase tracking-wide text-text-muted">Admins</p>
                    <p className="mt-2 text-xl font-bold text-text">{memberStats.admins}</p>
                  </div>
                  <div className="rounded-2xl border border-border bg-surface-elevated/40 p-4">
                    <p className="text-xs uppercase tracking-wide text-text-muted">Moderators</p>
                    <p className="mt-2 text-xl font-bold text-text">{memberStats.moderators}</p>
                  </div>
                  <div className="rounded-2xl border border-border bg-surface-elevated/40 p-4">
                    <p className="text-xs uppercase tracking-wide text-text-muted">Members</p>
                    <p className="mt-2 text-xl font-bold text-text">{memberStats.members}</p>
                  </div>
                </div>
                <div className="rounded-2xl border border-border bg-surface-elevated/40 p-4">
                  <p className="text-xs uppercase tracking-wide text-text-muted">Your Role</p>
                  <p className="mt-2 text-2xl font-bold text-text">{formatRoleLabel(squad.userRole)}</p>
                  <p className="mt-2 text-sm text-text-muted">
                    {squad.isMember
                      ? `You are part of this ${entityLabel} in the current context.`
                      : `You are viewing this ${entityLabel} shell without an active membership.`}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function SquadMemberPilotRatingDisclosure({
  squadId,
  targetUserId,
}: {
  squadId: number;
  targetUserId: number;
}) {
  const [requested, setRequested] = useState(false);
  const ratingQuery = useSquadMemberPilotRating(
    PILOT_UI_ENABLED && requested,
    squadId,
    targetUserId,
  );
  const headline = ratingQuery.data ? squadMemberPilotRatingHeadline(ratingQuery.data) : null;
  const evidence = ratingQuery.data ? ratingPilotEvidenceLabel(ratingQuery.data.evidenceState) : null;

  if (!PILOT_UI_ENABLED) return null;

  if (!requested) {
    return (
      <button
        type="button"
        className="text-left text-xs font-semibold text-primary hover:text-primary-hover focus:outline-none focus:ring-2 focus:ring-primary/30"
        onClick={() => setRequested(true)}
      >
        View rating
      </button>
    );
  }

  if (ratingQuery.isLoading) {
    return <p className="text-xs text-text-subtle-readable">Checking rating...</p>;
  }

  if (!headline || !evidence) {
    return <p className="text-xs text-text-subtle-readable">No squad-visible rating</p>;
  }

  return (
    <div className="flex flex-wrap items-center gap-2" aria-label={`TARK Rating ${headline}, ${evidence}`}>
      <span className="text-sm font-bold text-primary">{headline}</span>
      <span className="text-xs text-text-subtle-readable">TARK Rating · {evidence}</span>
    </div>
  );
}

function ReadOnlyConversationCard({
  squad,
  entityLabel,
  canReplyFromWeb,
}: {
  squad: Squad;
  entityLabel: string;
  canReplyFromWeb: boolean;
}) {
  const [draft, setDraft] = useState('');
  const [replyingTo, setReplyingTo] = useState<SquadChatMessage | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [optimisticMessages, setOptimisticMessages] = useState<SquadChatMessage[]>([]);
  const messagesQuery = useSquadMessages(squad.id, Boolean(squad.id), { pollMs: 15 * 1000 });
  const sendMessage = useSendSquadMessage(squad.id);
  const markRead = useMarkSquadRead(squad.id);
  const serverMessages = messagesQuery.data?.messages ?? [];
  const pendingClientIds = new Set(optimisticMessages.map((message) => message.clientMessageId).filter(Boolean));
  const messages = [
    ...serverMessages,
    ...optimisticMessages.filter((message) => !serverMessages.some((serverMessage) => serverMessage.clientMessageId === message.clientMessageId)),
  ];
  const unreadCount = squad.unreadCount ?? 0;
  const hasMessages = messages.length > 0;

  // Keep 64-bit chat IDs as strings. The server validates the visible boundary
  // against current chat state, so a newer unseen arrival cannot be marked read.
  const latestServerId = serverMessages.reduce<string | undefined>((latest, message) => {
    const id = String(message.id);
    if (!/^[1-9]\d*$/.test(id)) return latest;
    return latest == null || BigInt(id) > BigInt(latest) ? id : latest;
  }, undefined);
  const latestMessageRef = useVisibleConversationRead(
    squad.id, latestServerId, messagesQuery.dataUpdatedAt, markRead.mutateAsync,
  );

  useEffect(() => {
    if (!pendingClientIds.size) {
      return;
    }
    setOptimisticMessages((current) =>
      current.filter((message) => !serverMessages.some((serverMessage) => serverMessage.clientMessageId === message.clientMessageId))
    );
  }, [pendingClientIds.size, serverMessages]);

  const handleSend = async () => {
    const messageText = draft.trim();
    if (!messageText || sendMessage.isPending) {
      return;
    }

    const clientMessageId =
      typeof globalThis.crypto?.randomUUID === 'function'
        ? globalThis.crypto.randomUUID()
        : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const optimisticMessage: SquadChatMessage = {
      id: `pending-${clientMessageId}`,
      senderId: 0,
      senderName: 'You',
      messageText,
      messageType: 'TEXT',
      createdAt: new Date().toISOString(),
      createdAtEpochMs: Date.now(),
      clientMessageId,
      isOwnMessage: true,
      status: 'SENDING',
      replyToMessageId: replyingTo?.id ?? null,
      replyToMessage: replyingTo
        ? {
            id: replyingTo.id,
            senderId: replyingTo.senderId,
            senderName: replyingTo.senderName,
            messageText: getMessageDisplayText(replyingTo),
            messageType: replyingTo.messageType,
          }
        : null,
    };

    setDraft('');
    setReplyingTo(null);
    setSendError(null);
    setOptimisticMessages((current) => [...current, optimisticMessage]);

    try {
      await sendMessage.mutateAsync({
        messageText,
        clientMessageId,
        replyToMessageId: optimisticMessage.replyToMessageId,
      });
    } catch (error: any) {
      setOptimisticMessages((current) => current.filter((message) => message.clientMessageId !== clientMessageId));
      setDraft(messageText);
      setSendError(error?.response?.data?.message || error?.message || 'Could not send that message.');
    }
  };

  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-primary" />
          Conversation
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-surface-elevated/40 px-4 py-3">
          <p className="text-[11px] uppercase tracking-[0.18em] text-text-subtle">
            {hasMessages ? 'Recent messages' : 'Conversation status'}
          </p>
          {unreadCount > 0 ? (
            <span className="shrink-0 rounded-full bg-primary px-3 py-1 text-xs font-semibold [color:var(--color-on-primary)]">
              {unreadCount} unread
            </span>
          ) : (
            <span className="shrink-0 rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold text-text-muted">
              {canReplyFromWeb ? 'Live' : 'Read only'}
            </span>
          )}
        </div>

        <div className="max-h-[360px] space-y-3 overflow-y-auto rounded-2xl border border-border bg-background p-3">
          {messagesQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-text-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading conversation
            </div>
          ) : messagesQuery.isError ? (
            <p className="text-sm text-red-300">Could not load recent messages.</p>
          ) : hasMessages ? (
            messages.map((message) => {
              const replyPreview = resolveReplyPreview(message, messages);
              return (
                <div
                  key={message.clientMessageId || message.id}
                  ref={String(message.id) === latestServerId ? latestMessageRef : undefined}
                  data-chat-message-id={String(message.id)}
                  className={`rounded-2xl border p-3 ${
                    message.isOwnMessage
                      ? 'border-primary/30 bg-primary/10'
                      : 'border-border bg-surface-elevated/40'
                  }`}
                >
                  {replyPreview ? (
                    <div className="mb-2 rounded-xl border border-border bg-background/70 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-text-subtle">
                        {replyPreview.senderName}
                      </p>
                      <p className="mt-1 line-clamp-2 text-xs leading-5 text-text-muted">
                        {replyPreview.messageText || getMessageTypePlaceholder(replyPreview.messageType)}
                      </p>
                    </div>
                  ) : null}
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-text">{message.isOwnMessage ? 'You' : message.senderName}</p>
                      <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-text-muted">
                        {getMessageDisplayText(message)}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs text-text-subtle">{formatConversationTime(message.createdAt)}</span>
                  </div>
                  {canReplyFromWeb ? (
                    <button
                      type="button"
                      className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80"
                      onClick={() => setReplyingTo(message)}
                    >
                      <Reply className="h-3.5 w-3.5" />
                      Reply
                    </button>
                  ) : null}
                </div>
              );
            })
          ) : (
            <p className="text-sm leading-6 text-text-muted">
              Messages will appear here once this {entityLabel} has activity.
            </p>
          )}
        </div>

        {canReplyFromWeb ? (
          <div className="space-y-3 rounded-2xl border border-border bg-surface-elevated/40 p-3">
            {replyingTo ? (
              <div className="flex items-start justify-between gap-3 rounded-xl border border-border bg-background px-3 py-2">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-text-subtle">
                    Replying to {replyingTo.senderName}
                  </p>
                  <p className="mt-1 line-clamp-2 text-xs leading-5 text-text-muted">{getMessageDisplayText(replyingTo)}</p>
                </div>
                <button type="button" className="text-text-subtle hover:text-text" onClick={() => setReplyingTo(null)}>
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : null}
            <Textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Write a reply"
              className="min-h-[92px]"
              maxLength={4000}
              onKeyDown={(event) => {
                if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                  event.preventDefault();
                  void handleSend();
                }
              }}
            />
            {sendError ? <p className="text-sm text-red-300">{sendError}</p> : null}
            <div className="flex justify-end">
              <Button onClick={() => void handleSend()} disabled={!draft.trim() || sendMessage.isPending}>
                {sendMessage.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Send
              </Button>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function resolveReplyPreview(message: SquadChatMessage, messages: SquadChatMessage[]) {
  if (message.replyToMessage) {
    return message.replyToMessage;
  }
  if (!message.replyToMessageId) {
    return null;
  }
  const matched = messages.find((candidate) => candidate.id === message.replyToMessageId);
  if (!matched) {
    return null;
  }
  return {
    id: matched.id,
    senderId: matched.senderId,
    senderName: matched.senderName,
    messageText: getMessageDisplayText(matched),
    messageType: matched.messageType,
  };
}

function getMessageDisplayText(message: Pick<SquadChatMessage, 'messageText' | 'messageType'>): string {
  if (message.messageType === 'TEXT' || message.messageType === 'SYSTEM') {
    return message.messageText || '';
  }
  return getMessageTypePlaceholder(message.messageType);
}

function getMessageTypePlaceholder(messageType: SquadChatMessage['messageType']): string {
  switch (messageType) {
    case 'IMAGE':
      return 'Photo — view on mobile';
    case 'VIDEO':
      return 'Video — view on mobile';
    case 'AUDIO':
      return 'Voice message — play on mobile';
    case 'FILE':
      return 'File — view on mobile';
    case 'SESSION':
      return 'Session update';
    default:
      return 'Message';
  }
}

function formatConversationTime(value?: string | null): string {
  if (!value) {
    return '';
  }
  const timestamp = new Date(value);
  if (Number.isNaN(timestamp.getTime())) {
    return '';
  }
  return timestamp.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
